if (!customElements.get('content-grid')) {
  const DRAG_START_DISTANCE = 4;
  const SCROLL_EDGE_TOLERANCE = 1;
  const SCROLL_END_DELAY = 80;

  class ContentGrid extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') {
        if (this.onWindowResize && this.slider) {
          window.addEventListener('resize', this.onWindowResize);
        }

        return;
      }

      this.dataset.ready = 'true';
      this.slider = this.querySelector('.content-grid__grid');
      this.previousButton = this.querySelector('[data-slider-previous]');
      this.nextButton = this.querySelector('[data-slider-next]');
      this.dragPointerId = null;
      this.dragStartX = 0;
      this.dragStartScrollLeft = 0;
      this.hasDragged = false;
      this.suppressItemClick = false;
      this.targetSlideIndex = 0;
      this.scrollEndTimer = null;
      this.resizeFrame = null;
      this.onWindowResize = this.handleWindowResize.bind(this);

      this.onItemClick = this.onItemClick.bind(this);
      this.onDragStart = this.onDragStart.bind(this);
      this.onPreviousButtonClick = this.onPreviousButtonClick.bind(this);
      this.onNextButtonClick = this.onNextButtonClick.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onSliderScroll = this.onSliderScroll.bind(this);

      if (this.previousButton) {
        this.previousButton.addEventListener('click', this.onPreviousButtonClick);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.onNextButtonClick);
      }

      if (!this.slider) {
        return;
      }

      this.slider.addEventListener('click', this.onItemClick, true);
      this.slider.addEventListener('dragstart', this.onDragStart);
      this.slider.addEventListener('pointerdown', this.onPointerDown);
      this.slider.addEventListener('scroll', this.onSliderScroll, { passive: true });
      window.addEventListener('resize', this.onWindowResize);
      this.updateSliderButtons();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onWindowResize);

      if (this.resizeFrame !== null) {
        window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = null;
      }
    }

    handleWindowResize() {
      if (this.resizeFrame !== null) {
        return;
      }

      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.alignSliderAfterResize();
      });
    }

    alignSliderAfterResize() {
      if (!this.slider || this.dragPointerId !== null) {
        return;
      }

      const maximumScrollLeft = Math.max(this.slider.scrollWidth - this.slider.clientWidth, 0);

      if (this.slider.scrollLeft > maximumScrollLeft) {
        this.slider.scrollLeft = maximumScrollLeft;
      }

      const positions = this.getSlideScrollPositions();
      const slideIndex = this.getNearestSlideIndex();
      const scrollLeft = positions[slideIndex] ?? 0;

      this.targetSlideIndex = slideIndex;

      if (Math.abs(this.slider.scrollLeft - scrollLeft) > SCROLL_EDGE_TOLERANCE) {
        this.slider.scrollLeft = scrollLeft;
      }

      this.updateSliderButtons();
    }

    onItemClick(event) {
      if (!this.suppressItemClick) {
        return;
      }

      this.suppressItemClick = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    onDragStart(event) {
      event.preventDefault();
    }

    onPreviousButtonClick() {
      this.scrollBySlides(-1);
    }

    onNextButtonClick() {
      this.scrollBySlides(1);
    }

    onPointerDown(event) {
      if (event.pointerType === 'touch' || event.button !== 0) {
        return;
      }

      if (!this.isSliderScrollable()) {
        return;
      }

      this.suppressItemClick = false;
      this.dragPointerId = event.pointerId;
      this.dragStartX = event.clientX;
      this.dragStartScrollLeft = this.slider.scrollLeft;
      this.hasDragged = false;

      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      if (event.pointerId !== this.dragPointerId) {
        return;
      }

      const dragDistance = event.clientX - this.dragStartX;

      if (!this.hasDragged && Math.abs(dragDistance) < DRAG_START_DISTANCE) {
        return;
      }

      if (!this.hasDragged) {
        this.hasDragged = true;
        this.slider.classList.add('is-dragging');
        this.slider.style.scrollSnapType = 'none';
      }

      event.preventDefault();
      this.slider.scrollLeft = this.dragStartScrollLeft - dragDistance;
    }

    onPointerUp(event) {
      if (event.pointerId !== this.dragPointerId) {
        return;
      }

      const shouldSnap = this.hasDragged;

      this.dragPointerId = null;
      this.hasDragged = false;
      this.removeDragListeners();

      if (!shouldSnap) {
        this.endDrag();
        return;
      }

      this.suppressItemClick = true;
      this.scrollToSlide(this.getNearestSlideIndex());
    }

    removeDragListeners() {
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
    }

    endDrag() {
      this.slider.classList.remove('is-dragging');

      if (this.slider.style.scrollSnapType === '') {
        return;
      }

      this.slider.style.scrollSnapType = '';
    }

    onSliderScroll() {
      if (this.dragPointerId !== null) {
        return;
      }

      window.clearTimeout(this.scrollEndTimer);

      this.scrollEndTimer = window.setTimeout(() => {
        this.endDrag();
        this.targetSlideIndex = this.getNearestSlideIndex();
        this.updateSliderButtons();
      }, SCROLL_END_DELAY);
    }

    scrollBySlides(offset) {
      const currentIndex = this.targetSlideIndex ?? this.getNearestSlideIndex();

      this.scrollToSlide(currentIndex + offset);
    }

    scrollToSlide(index) {
      const positions = this.getSlideScrollPositions();

      if (positions.length === 0) {
        return;
      }

      const lastIndex = positions.length - 1;
      const clampedIndex = Math.min(Math.max(index, 0), lastIndex);
      const scrollLeft = positions[clampedIndex];

      this.targetSlideIndex = clampedIndex;
      this.updateSliderButtons();

      if (Math.abs(this.slider.scrollLeft - scrollLeft) <= SCROLL_EDGE_TOLERANCE) {
        this.endDrag();
        return;
      }

      this.slider.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
    }

    getNearestSlideIndex() {
      const positions = this.getSlideScrollPositions();
      const scrollLeft = this.slider.scrollLeft;
      let nearestIndex = 0;
      let nearestDistance = Infinity;

      positions.forEach((position, index) => {
        const distance = Math.abs(position - scrollLeft);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return nearestIndex;
    }

  getSlideScrollPositions() {
    const items = Array.from(this.slider.querySelectorAll('.content-grid__item'));

    if (items.length === 0) {
      return [];
    }

    const firstItemOffset = items[0].offsetLeft;
    const maximumScrollLeft = Math.max(this.slider.scrollWidth - this.slider.clientWidth, 0);
    const positions = [];

    items.forEach((item) => {
      const scrollLeft = Math.min(Math.max(item.offsetLeft - firstItemOffset, 0), maximumScrollLeft);
      const previousScrollLeft = positions.at(-1);

      if (previousScrollLeft !== undefined && Math.abs(scrollLeft - previousScrollLeft) <= SCROLL_EDGE_TOLERANCE) {
        return;
      }

      positions.push(scrollLeft);
    });

    return positions;
  }

    isSliderScrollable() {
      if (!this.slider) {
        return false;
      }

      return this.slider.scrollWidth > this.slider.clientWidth + SCROLL_EDGE_TOLERANCE;
    }

    updateSliderButtons() {
      const positions = this.slider ? this.getSlideScrollPositions() : [];
      const lastIndex = Math.max(positions.length - 1, 0);
      const slideIndex = this.targetSlideIndex ?? 0;

      if (this.previousButton) {
        this.previousButton.disabled = !this.isSliderScrollable() || slideIndex <= 0;
      }

      if (this.nextButton) {
        this.nextButton.disabled = !this.isSliderScrollable() || slideIndex >= lastIndex;
      }
    }
  }

  customElements.define('content-grid', ContentGrid);
}
